<script setup lang="ts">
defineProps<{ kbEnabled: boolean }>();
const emit = defineEmits<{ close: []; 'toggle-kb': [] }>();

// v4's literal shortcut list, plus the two modifier-based actions kept as
// an addition beyond the design (see the 2026-08-07 decision) — real,
// always-on functionality a reviewer would otherwise have no way to
// discover from this panel.
const SHORTCUTS: { keys: string; desc: string }[] = [
  { keys: '↑ ↓ / J K', desc: 'Move between supporting records' },
  { keys: '← →', desc: 'Previous / next hit' },
  { keys: 'Enter', desc: 'Open the focused record in the reader' },
  { keys: 'Esc', desc: 'Close the reader, sheet or this panel' },
  { keys: 'T / F / U', desc: 'Set verdict: true positive, false positive, unknown' },
  { keys: 'N', desc: 'Cycle the focused record through its statuses' },
  { keys: 'C', desc: 'Write a reviewer note' },
  { keys: 'O', desc: 'Open the source in a new tab' },
  { keys: '1 – 6', desc: 'Launch a tool (Google, Gemini, Wayback, Maps, LinkedIn, Zendesk)' },
  { keys: 'G', desc: 'Show or hide the right-hand pane' },
  { keys: 'R', desc: 'Switch the right-hand pane between Tools and Reference' },
  { keys: 'D', desc: 'Open the attached documents in full screen' },
  { keys: '← →', desc: 'In the document preview: previous / next document' },
  { keys: 'R / ⇧R', desc: 'In the document preview: rotate the page right / left' },
  { keys: '?', desc: 'Open this panel' },
  { keys: '⌘ / Ctrl S', desc: 'Save' },
  { keys: '⌘ / Ctrl Enter', desc: 'Submit the case' },
];
</script>

<template>
  <div
    class="fixed inset-0 z-[70] grid place-items-center bg-gray-900/45 p-6"
    data-test="shortcuts-modal"
    @click.self="emit('close')"
  >
    <div class="max-h-full w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-xl">
      <div class="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
        <div>
          <h3 class="text-[15px] font-semibold text-gray-900">Keyboard shortcuts</h3>
          <p class="mt-1 text-xs text-gray-500">
            Desktop only. Turn off if they clash with your screen reader or IME.
          </p>
        </div>
        <button
          type="button"
          data-test="btn-toggle-kb-modal"
          class="flex-none rounded-md px-3.5 py-1.5 text-xs font-semibold"
          :class="
            kbEnabled
              ? 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50'
              : 'bg-indigo-600 text-white hover:bg-indigo-700'
          "
          @click="emit('toggle-kb')"
        >
          {{ kbEnabled ? 'Turn off' : 'Turn on' }}
        </button>
      </div>
      <div class="px-5 py-2">
        <div
          v-for="s in SHORTCUTS"
          :key="s.keys"
          class="flex items-center gap-3 border-b border-gray-100 py-2 last:border-0"
        >
          <span class="w-32 flex-none font-mono text-[11.5px] font-semibold text-gray-900">{{
            s.keys
          }}</span>
          <span class="text-[12.5px] text-gray-600">{{ s.desc }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
